import { Form } from 'antd';
import { FC } from 'react';

import { TableHeaderCell } from '../../../../components/Table/TableHeaderCell/TableHeaderCell';
import { useAppSelector } from '../../../../lib/store/hooks';
import {
  clearListFilters,
  formatArrangedData,
  getArrangesInitialValues,
  getParsedSelectedItems,
  getSelectedItemsLabels,
} from '../../../../lib/utils/arranges-helpers';
import { useStringArrange } from '../../../../features/table/helpers/useStringArrange';
import { useTenantArrangeRequests } from '../../hooks/useTenantArrangeRequests';
import { useArrangeSubmit } from '../../../../features/table/helpers/useArrangeSubmit';
import { ArrangeListPopup } from '../../../../features/table/components/popups/ArrangeListPopup';
import { useLazyGetAllTenantsQuery } from '../../../../lib/store/api/tenants';

type Props = {
  arrangedId: string;
  title: string;
  searchFieldName: string;
  filterFieldName: string;
  headerCellProps?: any;
};

export const TenantSearchDataHeaderCell: FC<Props> = ({
  arrangedId,
  title,
  searchFieldName,
  filterFieldName,
  headerCellProps: additionalHeaderCellProps,
}) => {
  const {
    arrangeId,
    sorting,
    filtering: { filter },
  } = useAppSelector(({ tenants }) => tenants);
  const isActiveArrange = arrangeId === arrangedId;
  const [form] = Form.useForm();
  const getSelectedItems = () => getSelectedItemsLabels(form);

  const [findTenants] = useLazyGetAllTenantsQuery();

  const { filtering } = useStringArrange({
    form,
    request: (value: string) => {
      return findTenants({ search: value, searchFieldName }).unwrap();
    },
    formatResponse: (data: { content: any[] }) => formatArrangedData(data, arrangedId),
  });

  const clearFilters = () => {
    clearListFilters(form);
    filtering.clearOptions();
  };

  const { applyArranges, removeFilter, removeFilterValue } = useTenantArrangeRequests({
    arrangedId,
    clearFilters,
    getFilterLabel: () => getSelectedItems().join(', '),
    getFilters: () => ({ [filterFieldName]: getSelectedItems() }),
    form,
    filterFieldName,
  });

  const headerCellProps = {
    arrangedId,
    title,
    removeFilter,
    filterLabel: Array.isArray(filter) && filter.join(', '),
    sortOrder: sorting.sortOrder,
    isActiveArrange,
    form,
    applyArranges,
    getParsedItems: () => getParsedSelectedItems(form),
    removeFilterValue,
    ...additionalHeaderCellProps,
  };

  const { onFinish } = useArrangeSubmit(applyArranges);

  return (
    <TableHeaderCell {...headerCellProps}>
      <ArrangeListPopup
        initialValues={getArrangesInitialValues(form)}
        form={form}
        filtering={filtering}
        onFinish={onFinish}
        isActiveArrange={isActiveArrange}
      />
    </TableHeaderCell>
  );
};
